import type { Config, UserCount } from '@pin/schemas/common';
import type {
  AdminMuteUserPayload,
  AdminRedactTermPayload,
  AdminUnbanUserPayload,
  AdminUnbanUsersPayload,
  AdminUnmuteUserPayload,
  AdminUnmuteUsersPayload,
  BanUserPayload,
  ClearChatPayload,
  HistoryEvent,
  RedactMessagePayload,
  SendAdminMessagePayload,
  UserBanInfo,
  UserSub,
} from '@pin/schemas/rest';
import type { ChatMessageAdmin } from '@pin/schemas/socket';
import {
  catchError,
  firstValueFrom,
  NEVER,
  repeat,
  switchMap,
  timeout,
  timer,
} from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { withIdToken } from '../firebase/auth';
import { isVisible$ } from '../utils/rxjs.dom';
import {
  checkNoContentSuccess,
  HttpError,
  retryWithBackoff,
} from '../utils/rxjs.http';

const fetchUserCounts$ = withIdToken((token) =>
  fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/user/count`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    method: 'GET',
    selector: (r): Promise<UserCount> => {
      if (!r.ok) {
        throw new HttpError(r);
      }
      return r.json();
    },
  }).pipe(timeout(6_000), retryWithBackoff(3))
);

export const pollUserCounts = (errorDelay = 5_000, repeatDelay = 15_000) =>
  isVisible$.pipe(
    switchMap((visible) => {
      if (!visible) {
        return NEVER;
      }
      return fetchUserCounts$.pipe(
        catchError((_err: unknown, source) =>
          timer(errorDelay).pipe(switchMap(() => source))
        ),
        repeat({ delay: () => timer(repeatDelay) })
      );
    })
  );

export const fetchEventHistory = async ({
  startId,
  take,
}: {
  startId?: string;
  take: number;
}) => {
  const searchParams = new URLSearchParams();
  if (startId != null) {
    searchParams.append('startId', startId);
  }
  searchParams.append('take', take.toString());
  return await firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${import.meta.env.VITE_ADMIN_API_URL}/admin/events?${searchParams.toString()}`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          selector: (r): Promise<HistoryEvent[]> => {
            if (!r.ok) {
              throw new HttpError(r);
            }
            return r.json();
          },
        }
      ).pipe(timeout(6_000), retryWithBackoff(3))
    )
  );
};

export const fetchUserMessages = async (
  userId: string,
  {
    startId,
    take,
  }: {
    startId?: string;
    take: number;
  }
) => {
  const searchParams = new URLSearchParams();
  if (startId != null) {
    searchParams.append('startId', startId);
  }
  searchParams.append('take', take.toString());
  return await firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${import.meta.env.VITE_ADMIN_API_URL}/admin/user/${userId}/messages?${searchParams.toString()}`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          selector: (r): Promise<ChatMessageAdmin[]> => {
            if (!r.ok) {
              throw new HttpError(r);
            }
            return r.json();
          },
        }
      ).pipe(timeout(6_000), retryWithBackoff(3))
    )
  );
};

export const fetchUserSub = async (userId: string) =>
  (
    await firstValueFrom(
      withIdToken((token) =>
        fromFetch(
          `${import.meta.env.VITE_ADMIN_API_URL}/admin/users/${userId}/sub`,
          {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
            selector: (r): Promise<UserSub> => {
              if (!r.ok) {
                throw new HttpError(r);
              }
              return r.json();
            },
          }
        ).pipe(timeout(6_000), retryWithBackoff(3))
      )
    )
  ).sub;

export const sendAdminMessage = async (payload: SendAdminMessagePayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/message`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const clearChat = async (payload: ClearChatPayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/clear-chat`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const redactMessage = async (payload: RedactMessagePayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/redact/message`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const redactTerm = async (payload: AdminRedactTermPayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/redact/term`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const banUser = async (payload: BanUserPayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/ban/user`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const unBanUser = async (payload: AdminUnbanUserPayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/unban/user`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const unBanUsers = async (payload: AdminUnbanUsersPayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/unban/users`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const muteUser = async (payload: AdminMuteUserPayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/mute/user`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const unMuteUser = async (payload: AdminUnmuteUserPayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/unmute/user`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const unMuteUsers = async (payload: AdminUnmuteUsersPayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/unmute/users`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const pollUserBanInfo = (
  userId: string,
  errorDelay = 5_000,
  repeatDelay = 2_500
) =>
  isVisible$.pipe(
    switchMap((visible) => {
      if (!visible) {
        return NEVER;
      }
      return withIdToken((token) =>
        fromFetch(
          `${import.meta.env.VITE_ADMIN_API_URL}/admin/users/${userId}/ban-info`,
          {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
            selector: (r): Promise<UserBanInfo> => {
              if (!r.ok) throw new HttpError(r);
              return r.json();
            },
          }
        )
      ).pipe(
        catchError((_err: unknown, source) =>
          timer(errorDelay).pipe(switchMap(() => source))
        ),
        repeat({ delay: () => timer(repeatDelay) })
      );
    })
  );

export const updateConfig = async (payload: Config) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/config`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};
